import { useState } from "react";
import { cn } from "@/lib/utils";
import { HoverSpotlight } from "@/components/ui/hover-spotlight";
import { Cpu, Rocket, Lightbulb, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const features: FeatureProps[] = [
  {
    icon: <Cpu className="h-7 w-7" />,
    title: "Custom Software & Automations",
    description: "Get your business idea transformed into a working MVP in weeks, not months. Take full ownership with no ongoing fees."
  },
  {
    icon: <Rocket className="h-7 w-7" />,
    title: "AI Bootcamp",
    description: "Master practical AI skills in just 5 days. Walk away with templates you can implement immediately. Limited to 10 participants per cohort."
  },
  {
    icon: <Lightbulb className="h-7 w-7" />,
    title: "AI Strategy Consulting",
    description: "Convert challenges into opportunities with actionable AI roadmaps. Our framework delivers ROI where others offer only theory."
  }
];

const Services = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="services" className="py-24 md:py-32 bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container max-w-7xl mx-auto px-4 md:px-6 relative">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore how we can help transform your business with AI-powered solutions
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <HoverSpotlight
                className={cn(
                  "relative p-8 rounded-2xl transition-all duration-500 h-full",
                  "border border-slate-100 hover:border-primary/20",
                  "bg-white/80 backdrop-blur-sm",
                  hoveredIndex === index ? "shadow-xl scale-[1.02]" : "shadow-md"
                )}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="flex flex-col h-full items-center text-center">
                  <div className={cn(
                    "flex-shrink-0 h-16 w-16 rounded-full flex items-center justify-center mb-6",
                    "transition-all duration-500",
                    hoveredIndex === index ? "bg-primary/20 scale-110" : "bg-primary/10"
                  )}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </HoverSpotlight>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-20"
        >
          <h3 className="text-2xl md:text-3xl font-bold font-display mb-6">
            Ready to Transform Your Business?
          </h3>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Book a free consultation call to discuss how we can help you leverage AI for your business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className={cn(
                "rounded-full px-8 text-white shadow-lg hover:shadow-xl transition-all duration-300",
                "gradient-button hover:scale-105"
              )}
              onClick={() => window.open('https://cal.com/niumora-ai/project-consultation', '_blank')}
            >
              Book Free Call <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="rounded-full px-8 hover:scale-105 transition-all duration-300"
              onClick={() => {
                const pricingSection = document.getElementById('pricing');
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              View Pricing
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
